import { useState, useRef, useEffect, useCallback } from "react";
import api from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import GuardianConsentSection from "@/components/GuardianConsentSection";
import { emptyGuardian, guardianPayload, resolveGuardianSection, validateGuardian } from "@/lib/guardian";
import PDFAnnotationToolbar from "@/components/PDFAnnotationToolbar";
import PDFAnnotationLayer from "@/components/PDFAnnotationLayer";
import PDFTextAnnotation from "@/components/PDFTextAnnotation";
import { Check, AlertCircle, FileText, Calendar, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RefreshCw } from "lucide-react";
import { initPdfWorker, loadPdfDocument, getSafeErrorMessage } from "@/config/pdfConfig";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

/**
 * PDFFormViewerWithAnnotations - PDF form viewer with annotation support
 * 
 * For uploaded PDF forms (is_uploaded_pdf=true):
 * - Renders PDF pages as canvas with annotation overlay
 * - Supports text and drawing annotations
 * - Autosaves drafts
 * - Generates flattened PDF on submission
 * 
 * For generated forms: This component should NOT be used.
 * Use DeviceFormMode.js instead.
 */
export default function PDFFormViewerWithAnnotations({
  template,
  formAssignmentId, // Required for annotations
  patientId,
  appointmentId,
  patientName,
  clinicName,
  clinicLogo,
  onComplete,
  onCancel,
  minorInfo,
  guardianSection: guardianSectionProp,
}) {
  // Form state
  const [acknowledged, setAcknowledged] = useState(false);
  const [consented, setConsented] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [guardian, setGuardian] = useState(emptyGuardian());
  const [guardianSignature, setGuardianSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const signatureRef = useRef(null);
  const guardianSignatureRef = useRef(null);

  // Patient-facing mode: hide the clinic shell while this form is open
  useEffect(() => {
    document.body.classList.add("lf-device-form-open");
    return () => document.body.classList.remove("lf-device-form-open");
  }, []);

  // PDF rendering state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null); // null or error code string
  const [pageImages, setPageImages] = useState([]); // Rendered page images
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef(null);

  // Annotation state
  const [annotations, setAnnotations] = useState([]);
  const [activeTool, setActiveTool] = useState("select");
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fontSize, setFontSize] = useState(14);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [editingTextAnnotation, setEditingTextAnnotation] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, saved, error

  // Undo/Redo state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const requiresConsent = !!template?.pdf_consent_text;
  const requiresSignature = template?.pdf_requires_signature !== false;
  const guardianSection = resolveGuardianSection(template, minorInfo, guardianSectionProp);
  const guardianRequired = Boolean(guardianSection.show && guardianSection.required);
  const patientSignatureRequired = requiresSignature && !guardianRequired;
  const acknowledgementText = template?.pdf_acknowledgement_text || "I confirm that I have read and understood this document.";
  const consentText = template?.pdf_consent_text;

  // Load PDF and render pages
  const loadPdf = useCallback(async () => {
    if (!template?.id) return;

    setPdfLoading(true);
    setPdfError(null);

    try {
      // Initialize PDF.js worker first
      const workerResult = initPdfWorker();
      if (!workerResult.success) {
        throw new Error(workerResult.error);
      }

      // Fetch PDF as blob
      const response = await api.get(`/forms/pdf/${template.id}`, {
        responseType: "arraybuffer",
      });

      // Load PDF with central config
      const pdf = await loadPdfDocument(response.data);
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);

      // Render all pages
      const images = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const scale = 1.5; // Base scale for quality
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;

        images.push({
          dataUrl: canvas.toDataURL("image/png"),
          width: viewport.width,
          height: viewport.height,
        });
      }
      setPageImages(images);
    } catch (err) {
      console.error("[PDFViewer] Failed to load PDF:", err);
      // Store safe error code
      setPdfError(err.message || "PDF_LOAD_FAILED");
    } finally {
      setPdfLoading(false);
    }
  }, [template?.id]);

  useEffect(() => {
    loadPdf();
  }, [loadPdf]);

  // Load existing annotations
  useEffect(() => {
    const loadAnnotations = async () => {
      if (!formAssignmentId) return;

      try {
        const { data } = await api.get(`/forms/annotations/${formAssignmentId}`);
        if (data.annotations && data.annotations.length > 0) {
          setAnnotations(data.annotations);
          // Initialize history with loaded annotations
          setHistory([data.annotations]);
          setHistoryIndex(0);
        }
      } catch (err) {
        // No existing annotations, that's fine
        console.log("No existing annotations");
      }
    };

    loadAnnotations();
  }, [formAssignmentId]);

  // Debounced autosave
  const saveTimeoutRef = useRef(null);
  const lastSavedRef = useRef(JSON.stringify([]));

  useEffect(() => {
    if (!formAssignmentId) return;

    const currentState = JSON.stringify(annotations);
    if (currentState === lastSavedRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setSaveStatus("saving");

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await api.post("/forms/annotations/draft", {
          form_assignment_id: formAssignmentId,
          annotations: annotations,
        });
        lastSavedRef.current = currentState;
        setSaveStatus("saved");

        // Clear "saved" status after 2 seconds
        setTimeout(() => {
          setSaveStatus((s) => (s === "saved" ? "idle" : s));
        }, 2000);
      } catch (err) {
        console.error("Autosave failed:", err);
        setSaveStatus("error");
      }
    }, 1000); // 1 second debounce

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [annotations, formAssignmentId]);

  // Add annotation with history
  const addAnnotation = useCallback((annotation) => {
    setAnnotations((prev) => {
      const newAnnotations = [...prev, annotation];
      // Update history
      setHistory((h) => [...h.slice(0, historyIndex + 1), newAnnotations]);
      setHistoryIndex((i) => i + 1);
      return newAnnotations;
    });
    
    // If adding text annotation, open editor immediately
    if (annotation.type === "text" && !annotation.text) {
      setEditingTextAnnotation(annotation);
      setSelectedAnnotationId(annotation.id);
    }
  }, [historyIndex]);

  // Update annotation
  const updateAnnotation = useCallback((id, updates) => {
    setAnnotations((prev) => {
      const newAnnotations = prev.map((a) =>
        a.id === id ? { ...a, ...updates, updated_at: new Date().toISOString() } : a
      );
      setHistory((h) => [...h.slice(0, historyIndex + 1), newAnnotations]);
      setHistoryIndex((i) => i + 1);
      return newAnnotations;
    });
  }, [historyIndex]);

  // Delete annotation
  const deleteAnnotation = useCallback((id) => {
    setAnnotations((prev) => {
      const newAnnotations = prev.filter((a) => a.id !== id);
      setHistory((h) => [...h.slice(0, historyIndex + 1), newAnnotations]);
      setHistoryIndex((i) => i + 1);
      return newAnnotations;
    });
    if (selectedAnnotationId === id) {
      setSelectedAnnotationId(null);
    }
  }, [historyIndex, selectedAnnotationId]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex((i) => i - 1);
      setAnnotations(history[historyIndex - 1]);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex((i) => i + 1);
      setAnnotations(history[historyIndex + 1]);
    }
  }, [history, historyIndex]);

  // Clear current page
  const handleClearPage = useCallback(() => {
    if (window.confirm("Clear all annotations on this page?")) {
      setAnnotations((prev) => {
        const newAnnotations = prev.filter((a) => a.page !== currentPage);
        setHistory((h) => [...h.slice(0, historyIndex + 1), newAnnotations]);
        setHistoryIndex((i) => i + 1);
        return newAnnotations;
      });
    }
  }, [currentPage, historyIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "z") {
          e.preventDefault();
          handleUndo();
        } else if (e.key === "y") {
          e.preventDefault();
          handleRedo();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Text annotation editor handlers
  const handleStartTextEdit = (annotation) => {
    setEditingTextAnnotation(annotation);
  };

  const handleSaveTextAnnotation = (id, updates) => {
    updateAnnotation(id, updates);
    setEditingTextAnnotation(null);
    // Keep the annotation selected after saving so user can immediately drag it
    setSelectedAnnotationId(id);
    // Auto-switch to select mode for immediate dragging capability
    if (activeTool === "text") {
      setActiveTool("select");
    }
  };

  const handleDeleteTextAnnotation = (id) => {
    deleteAnnotation(id);
    setEditingTextAnnotation(null);
  };

  // Validation
  const validateForm = () => {
    if (!acknowledged) {
      setValidationError("Please confirm you have read and understood the document.");
      return false;
    }
    if (requiresConsent && !consented) {
      setValidationError("Please provide your consent to continue.");
      return false;
    }
    if (patientSignatureRequired && (!signatureData || !signatureData.data)) {
      setValidationError("Please provide your signature to complete this form.");
      return false;
    }
    const latestGuardianSignature = guardianSignatureRef.current?.getSignatureData() || guardianSignature;
    const guardianError = validateGuardian(guardian, guardianRequired, latestGuardianSignature);
    if (guardianError) {
      setValidationError(guardianError);
      return false;
    }
    setValidationError("");
    return true;
  };

  // Submit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let finalSignature = signatureData;
      if (signatureRef.current) {
        const sigData = signatureRef.current.getSignatureData();
        if (sigData) {
          finalSignature = sigData;
        }
      }

      const today = new Date().toISOString().split("T")[0];

      // Save final annotations
      if (formAssignmentId && annotations.length > 0) {
        await api.post("/forms/annotations/draft", {
          form_assignment_id: formAssignmentId,
          annotations: annotations,
        });
      }

      // Submit form
      const payload = {
        template_id: template.id,
        patient_id: patientId,
        appointment_id: appointmentId,
        form_assignment_id: formAssignmentId,
        responses: {
          acknowledgement: acknowledged,
          acknowledgement_text: acknowledgementText,
          consent: requiresConsent ? consented : null,
          consent_text: consentText || null,
          date_signed: today,
          has_annotations: annotations.length > 0,
        },
        status: "completed",
        signature_type: finalSignature?.type || null,
        signature_data: finalSignature?.type === "drawn" ? finalSignature.data : null,
        typed_signature: finalSignature?.type === "typed" ? finalSignature.data : null,
        signature: finalSignature?.type === "typed" ? finalSignature.data : null,
        completed_on_device: true,
        guardian: guardianSection.show
          ? guardianPayload(guardian, guardianSignatureRef.current?.getSignatureData() || guardianSignature)
          : null,
      };

      await api.post("/forms/submissions", payload);
      setShowSuccess(true);

      setTimeout(() => {
        onComplete?.();
      }, 2000);
    } catch (err) {
      setValidationError("There was a problem submitting the form. Please try again.");
    }
    setSubmitting(false);
  };

  const handleSignatureChange = (data) => {
    setSignatureData(data.isEmpty ? null : data);
    if (validationError && data.data) {
      setValidationError("");
    }
  };

  // Get annotations for current page
  const currentPageAnnotations = annotations.filter((a) => a.page === currentPage);

  // Success state
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-[10000] bg-[#FAF9F6] flex flex-col items-center justify-center p-6">
        <div className="w-20 h-20 rounded-full bg-[#205B5A]/10 flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-[#205B5A]" strokeWidth={2} />
        </div>
        <h1 className="text-2xl font-medium text-[#1A1B1A] mb-2" style={H}>
          Form submitted
        </h1>
        <p className="text-sm text-[#757675] text-center">
          Thank you for completing the form, {patientName?.split(" ")[0] || "Patient"}.
        </p>
        <p className="text-xs text-[#757675] mt-4">Returning to reception...</p>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[10000] bg-[#FAF9F6] flex flex-col"
      data-testid="pdf-form-viewer-annotations"
    >
      {/* Header */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {clinicLogo ? (
            <img src={clinicLogo} alt="" className="h-8 w-auto" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-[#205B5A]/10 flex items-center justify-center">
              <FileText className="w-4 h-4 text-[#205B5A]" />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-[#1A1B1A]" style={H}>
              {clinicName || "Clinic"}
            </p>
            <p className="text-xs text-[#757675]">{template?.name || "Document"}</p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-xs text-[#757675] hover:text-[#1A1B1A] px-3 py-1.5 rounded-lg hover:bg-[#FAF9F6] border border-[#1A1B1A]/10"
          data-testid="pdf-form-cancel"
        >
          Return to reception
        </button>
      </div>

      {/* Patient greeting */}
      <div className="bg-white px-4 sm:px-6 py-3 border-b border-[#1A1B1A]/[0.06]">
        <p className="text-sm text-[#1A1B1A]">
          Hello, <span className="font-medium">{patientName || "Patient"}</span>. Please review and complete the document below, then sign.
        </p>
      </div>

      {/* Annotation Toolbar */}
      <div className="px-4 sm:px-6 py-2 bg-white border-b border-[#1A1B1A]/[0.06]">
        <PDFAnnotationToolbar
          activeTool={activeTool}
          onToolChange={setActiveTool}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onClearPage={handleClearPage}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history.length - 1}
          saveStatus={saveStatus}
          strokeWidth={strokeWidth}
          onStrokeWidthChange={setStrokeWidth}
          fontSize={fontSize}
          onFontSizeChange={setFontSize}
        />
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="bg-red-50 px-4 sm:px-6 py-3 border-b border-red-100" data-testid="pdf-form-error">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-600">{validationError}</p>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
        {/* PDF Viewer with Annotations */}
        <div
          ref={containerRef}
          className="flex-1 overflow-auto bg-[#3d3d3d] p-4"
          data-testid="pdf-container"
        >
          {pdfLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3" />
                <p className="text-white/60 text-sm">Loading document...</p>
              </div>
            </div>
          ) : pageImages.length > 0 ? (
            <div className="max-w-4xl mx-auto">
              {/* Page navigation */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-white text-sm">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage === totalPages - 1}
                    className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-30 hover:bg-white/20"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  {/* Zoom controls */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                      className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="text-white text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
                    <button
                      onClick={() => setZoom((z) => Math.min(2, z + 0.25))}
                      className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* PDF Page with Annotation Layer */}
              <div
                className="relative bg-white rounded-lg shadow-lg overflow-hidden mx-auto"
                style={{
                  width: pageImages[currentPage]?.width * zoom * 0.5,
                  height: pageImages[currentPage]?.height * zoom * 0.5,
                }}
              >
                {/* PDF Page Image */}
                <img
                  src={pageImages[currentPage]?.dataUrl}
                  alt={`Page ${currentPage + 1}`}
                  className="w-full h-full object-contain"
                  draggable={false}
                />

                {/* Annotation Layer */}
                <PDFAnnotationLayer
                  pageNumber={currentPage}
                  pageWidth={pageImages[currentPage]?.width * zoom * 0.5}
                  pageHeight={pageImages[currentPage]?.height * zoom * 0.5}
                  annotations={currentPageAnnotations}
                  activeTool={activeTool}
                  strokeWidth={strokeWidth}
                  strokeColor="#000000"
                  fontSize={fontSize}
                  onAddAnnotation={addAnnotation}
                  onUpdateAnnotation={updateAnnotation}
                  onDeleteAnnotation={deleteAnnotation}
                  selectedAnnotationId={selectedAnnotationId}
                  onSelectAnnotation={setSelectedAnnotationId}
                  onStartTextEdit={handleStartTextEdit}
                />
              </div>

              {/* Page thumbnails for multi-page */}
              {totalPages > 1 && (
                <div className="flex gap-2 mt-4 justify-center flex-wrap">
                  {pageImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPage(idx)}
                      className={`relative w-16 h-20 rounded border-2 overflow-hidden ${
                        idx === currentPage ? "border-white" : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img src={img.dataUrl} alt={`Page ${idx + 1}`} className="w-full h-full object-cover" />
                      {annotations.filter((a) => a.page === idx).length > 0 && (
                        <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-[#205B5A] rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : pdfError ? (
            <div className="flex items-center justify-center h-full" data-testid="pdf-error-state">
              <div className="text-center py-8 max-w-sm">
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
                <p className="text-white/80 mb-2 font-medium">Unable to load the PDF document</p>
                <p className="text-white/50 text-sm mb-6">
                  {getSafeErrorMessage(pdfError)}
                </p>
                <button
                  onClick={loadPdf}
                  className="px-6 py-2.5 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2 mx-auto"
                  data-testid="pdf-retry-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-white/60">No PDF available</p>
            </div>
          )}
        </div>

        {/* Acknowledgement & Signature Panel */}
        <div className="w-full lg:w-[400px] bg-white border-t lg:border-t-0 lg:border-l border-[#1A1B1A]/[0.06] flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
            <div>
              <h3 className="text-sm font-medium text-[#1A1B1A] mb-3" style={H}>
                Acknowledgement & Signature
              </h3>
              <p className="text-xs text-[#757675]">
                Please confirm you have read the document and provide your signature.
              </p>
              {guardianRequired && (
                <p className="mt-2 text-xs text-[#205B5A] bg-[#205B5A]/[0.06] border border-[#205B5A]/20 rounded-xl px-3 py-2" data-testid="minor-patient-notice">
                  {guardianSection.notice}
                </p>
              )}
            </div>

            {/* Acknowledgement checkbox */}
            <label
              className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                acknowledged
                  ? "border-[#205B5A] bg-[#205B5A]/5"
                  : "border-[#1A1B1A]/10 hover:border-[#205B5A]/30"
              }`}
              data-testid="acknowledgement-checkbox"
            >
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => {
                  setAcknowledged(e.target.checked);
                  if (validationError) setValidationError("");
                }}
                className="w-5 h-5 rounded border-[#1A1B1A]/20 text-[#205B5A] mt-0.5 flex-shrink-0"
              />
              <span className="text-sm text-[#1A1B1A] leading-relaxed">{acknowledgementText}</span>
            </label>

            {/* Consent checkbox (if configured) */}
            {requiresConsent && (
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                  consented
                    ? "border-[#205B5A] bg-[#205B5A]/5"
                    : "border-[#1A1B1A]/10 hover:border-[#205B5A]/30"
                }`}
                data-testid="consent-checkbox"
              >
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(e) => {
                    setConsented(e.target.checked);
                    if (validationError) setValidationError("");
                  }}
                  className="w-5 h-5 rounded border-[#1A1B1A]/20 text-[#205B5A] mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-[#1A1B1A] leading-relaxed">{consentText}</span>
              </label>
            )}

            {/* Signature */}
            {requiresSignature && (
              <div>
                <SignaturePad
                  ref={signatureRef}
                  label={guardianRequired ? "Patient signature (optional)" : "Your signature"}
                  required={!guardianRequired}
                  onChange={handleSignatureChange}
                  initialMode="draw"
                  compact={true}
                />
                {guardianRequired && (
                  <p className="mt-2 text-xs text-[#4A4B4A]" data-testid="patient-signature-optional-note">
                    {guardianSection.patient_signature_note}
                  </p>
                )}
                <div
                  className="mt-3 flex items-center gap-2 text-sm text-[#757675] bg-[#FAF9F6] rounded-xl px-3 py-2.5"
                  data-testid="pdf-date-signed-indicator"
                >
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>
                    Date signed: <strong className="text-[#1A1B1A]">{new Date().toLocaleDateString("en-GB")}</strong>{" "}
                    <span className="text-[#9A9B9A] text-xs">(auto)</span>
                  </span>
                </div>
              </div>
            )}

            <GuardianConsentSection
              section={guardianSection}
              value={guardian}
              onChange={(g) => { setGuardian(g); if (validationError) setValidationError(""); }}
              signatureRef={guardianSignatureRef}
              onSignatureChange={(data) => {
                setGuardianSignature(data.isEmpty ? null : data);
                if (validationError && data.data) setValidationError("");
              }}
            />
          </div>

          {/* Submit button */}
          <div className="p-4 sm:p-6 border-t border-[#1A1B1A]/[0.06] bg-[#FAF9F6]">
            <button
              onClick={handleSubmit}
              disabled={submitting || !acknowledged || (requiresConsent && !consented)}
              className="w-full bg-[#205B5A] text-white py-3 rounded-xl text-sm font-medium hover:bg-[#174544] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="pdf-form-submit"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Submit form
                </>
              )}
            </button>
            <p className="text-[10px] text-[#757675] text-center mt-2.5" data-testid="form-audit-privacy-note">
              Your information is stored securely and used only for the purpose of providing clinical care.
            </p>
          </div>
        </div>
      </div>

      {/* Text annotation editor modal */}
      {editingTextAnnotation && (
        <PDFTextAnnotation
          annotation={editingTextAnnotation}
          onSave={handleSaveTextAnnotation}
          onCancel={() => setEditingTextAnnotation(null)}
          onDelete={handleDeleteTextAnnotation}
        />
      )}
    </div>
  );
}
